/**
 * Product grid / list renderer. Switches between the mobile row layout and
 * the desktop card grid, and renders a row of skeletons while loading, and
 * an empty state with a "clear filters" reset.
 *
 * The desktop card grid uses CSS `auto-fill` + `minmax(min(240px, 100%), 1fr)`
 * so the column count scales smoothly with the available width — no fixed
 * `sm:/lg:/xl:` step changes. The `min(240px, 100%)` clamp prevents
 * overflow when the container itself is narrower than the card minimum.
 */

"use client";

import { useEffect, useRef } from "react";
import { ProductCardSkeleton } from "@/components/skeletons/ProductCardSkeleton";
import type { Product } from "@/stores/product-store";
import { trackSelectItem, trackViewItemList } from "@/lib/analytics";
import { toAnalyticsListItems } from "./list-analytics";
import { ProductCard } from "../card/ProductCard";
import { ProductCardMobile } from "../card/ProductCardMobile";
import { CustomOrderPrompt } from "../CustomOrderPrompt";

interface ProductGridProps {
  products: Product[];
  loading: boolean;
  useMobileLayout: boolean;
  onOpenProduct: (p: Product) => void;
  onAddToCart: (p: Product) => void;
  onZoom: (p: Product) => void;
  onClearFilters: () => void;
  /**
   * GA4 list attribution. `listId` must be a STABLE slug naming the surface
   * (`products-grid`, `carousel-crochet`, …) — it's the key that makes "which
   * page drove this product click" answerable, so it must not churn per filter
   * change. `listName` is the human label.
   */
  listId?: string;
  listName?: string;
}

/**
 * Tailwind class for the auto-fit responsive grid. Centralised so the
 * loading skeleton and the rendered grid stay in sync.
 *   - `auto-fill` packs as many cards as fit; cards stretch to share
 *     leftover space via the `1fr` upper bound.
 *   - `min(240px, 100%)` is the column minimum width; the inner `min`
 *     stops a single card overflowing when the container itself is
 *     narrower than 240px (e.g. inside the iPhone-SE rail layout).
 *   - Gap scales: 12px on phones, 16px on tablets, 20px on desktop.
 */
const GRID_CLASS =
  "grid gap-3 sm:gap-4 md:gap-5 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4";

export function ProductGrid({
  products,
  loading,
  useMobileLayout,
  onOpenProduct,
  onAddToCart,
  onZoom,
  onClearFilters,
  listId = "products-grid",
  listName = "Products grid",
}: ProductGridProps) {
  const listRef = useRef<HTMLDivElement | null>(null);
  // Fingerprint of what's on screen — one `view_item_list` per distinct page /
  // filter result, never one per re-render.
  const reportedRef = useRef<string>("");

  useEffect(() => {
    if (loading || products.length === 0) return;
    const key = `${listId}|${products.map((p) => p.id).join(",")}`;
    if (reportedRef.current === key) return;
    const el = listRef.current;
    if (!el) return;

    let cancelled = false;
    const fire = () => {
      if (cancelled || reportedRef.current === key) return;
      reportedRef.current = key;
      trackViewItemList(listId, listName, toAnalyticsListItems(products));
    };

    // No IntersectionObserver (jsdom without a mock, very old browsers) → just
    // report. A missing impression is worse than an unverified one.
    if (typeof IntersectionObserver === "undefined") {
      fire();
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          fire();
          io.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    io.observe(el);
    return () => {
      cancelled = true;
      io.disconnect();
    };
  }, [loading, products, listId, listName]);

  /** Card click → `select_item` from THIS list, then the normal open. */
  const handleOpen = (p: Product, index: number) => {
    trackSelectItem(listId, listName, toAnalyticsListItems([p], index)[0]!);
    onOpenProduct(p);
  };

  if (loading) {
    return useMobileLayout ? (
      <div className="mt-6 flex flex-col gap-2 min-[400px]:gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <ProductCardSkeleton key={i} variant="list" />
        ))}
      </div>
    ) : (
      <div className={`mt-8 ${GRID_CLASS}`}>
        {Array.from({ length: 8 }).map((_, i) => (
          <ProductCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
        {/* Highest-intent "can't find it" moment → lead with the bespoke CTA. */}
        <CustomOrderPrompt variant="empty" />
        <button
          type="button"
          onClick={onClearFilters}
          className="inline-flex min-h-11 items-center text-sm text-primary underline sm:min-h-0"
        >
          Clear all filters
        </button>
      </div>
    );
  }

  if (useMobileLayout) {
    return (
      <div ref={listRef} className="mt-6 flex flex-col gap-2 min-[400px]:gap-3">
        {products.map((p, index) => (
          <ProductCardMobile
            key={p.id}
            product={p}
            onClick={() => handleOpen(p, index)}
            onAddToCart={(product) => onAddToCart(product)}
            onZoom={() => onZoom(p)}
          />
        ))}
      </div>
    );
  }

  return (
    <div ref={listRef} className={`mt-8 ${GRID_CLASS}`}>
      {products.map((p, index) => (
        <ProductCard
          key={p.id}
          product={p}
          // Eager-load only the first ROW (4 cards on the desktop lg grid) so
          // above-the-fold images aren't deferred by next/image's default lazy
          // loading (helps LCP), WITHOUT firing 8 full-resolution R2 fetches
          // that block first paint. The rest lazy-load as the user scrolls.
          // Until CF Image Resizing is enabled these are full-size originals
          // (~450KB each), so keeping the eager count low is the cheap win.
          eager={index < 4}
          onClick={() => handleOpen(p, index)}
          onAddToCart={(product) => onAddToCart(product)}
          onZoom={() => onZoom(p)}
        />
      ))}
    </div>
  );
}
